import type { SealedProduct } from "@hobbyshopify/backend-client";
import { formatCents } from "../core/primitives/card-display";
import type { LoadStatus } from "../core/primitives/load-status";

/** What the scan read, set beside the picked item so staff can check the two are one box. */
export type ScanIdentity = { setName: string; year: string; productType: string; barcode: string | null };

const formatLabel = (format: string) => format.replace(/_/g, " ").toLowerCase();

function ItemLine({ item }: { item: SealedProduct }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="h-14 w-11 shrink-0 overflow-hidden rounded border border-line bg-surface-strong">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-faint">no photo</div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-white">{item.name}</div>
        <div className="truncate text-xs text-muted">
          {[item.setName, item.year, formatLabel(item.format)].filter(Boolean).join(" · ")}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-faint">
          <span>UPC {item.barcode || "—"}</span>
          <span>
            {item.onHand} on hand / {item.reserved} held / {item.available} available
          </span>
          <span>cost {item.costCents == null ? "—" : formatCents(item.costCents)}</span>
          <span>added {item.createdAt.toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

function Comparison({ item, scan }: { item: SealedProduct; scan: ScanIdentity }) {
  const rows: [string, string, string][] = [
    ["Set", scan.setName, item.setName ?? ""],
    ["Year", scan.year, item.year != null ? String(item.year) : ""],
    ["Type", formatLabel(scan.productType), formatLabel(item.format)],
    ["Barcode", scan.barcode ?? "", item.barcode ?? ""],
  ];
  return (
    <table className="w-full text-xs">
      <thead className="text-faint">
        <tr>
          <th className="text-left font-normal" />
          <th className="text-left font-normal">Scan</th>
          <th className="text-left font-normal">Item</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, read, held]) => {
          const differs = read !== "" && held !== "" && read.toLowerCase() !== held.toLowerCase();
          return (
            <tr key={label} className={differs ? "text-warn" : "text-muted"}>
              <td className="py-0.5 pr-3">{label}</td>
              <td className="py-0.5 pr-3">{read || "—"}</td>
              <td className="py-0.5">{held || "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Links a sealed scan to a box the shop typed in by hand, so confirming keeps
 * that item, its lots and its breaks instead of making a second one. Nothing is
 * picked for the reviewer: a similar name is not the same box.
 */
export function LinkSealedPicker({
  search,
  status,
  candidates,
  matched,
  selected,
  conflict,
  scan,
  scanName,
  marketCents,
  adoptName,
  adoptPrice,
  disabled,
  onSearch,
  onPick,
  onClear,
  onToggleName,
  onTogglePrice,
}: {
  search: string;
  status: LoadStatus;
  candidates: readonly SealedProduct[];
  /** False when nothing matched the search and the list is every hand-made item. */
  matched: boolean;
  selected: SealedProduct | null;
  conflict: string | null;
  scan: ScanIdentity;
  /** The name the scan read, which the item takes when `adoptName` is on. */
  scanName: string | null;
  marketCents: number | null;
  adoptName: boolean;
  adoptPrice: boolean;
  disabled: boolean;
  onSearch: (term: string) => void;
  onPick: (itemId: string) => void;
  onClear: () => void;
  onToggleName: () => void;
  onTogglePrice: () => void;
}) {
  const money = (cents: number | null) => (cents == null ? "—" : formatCents(cents));
  return (
    <section className="mb-5 rounded-row border border-line bg-surface p-4">
      <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Link to an item you added by hand</h3>
      {selected ? (
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <ItemLine item={selected} />
            <button type="button" onClick={onClear} disabled={disabled} className="btn-ghost shrink-0">
              Change
            </button>
          </div>
          <Comparison item={selected} scan={scan} />
          {conflict && <p className="note note-danger text-sm">{conflict}</p>}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-muted">
              <input type="checkbox" checked={adoptName} disabled={disabled || !scanName} onChange={onToggleName} />
              <span>Use the scan's name</span>
              {scanName && (
                <span className="truncate text-xs text-faint">
                  {selected.name} → {scanName}
                </span>
              )}
            </label>
            <label className="flex items-center gap-2 text-sm text-muted">
              <input
                type="checkbox"
                checked={adoptPrice}
                disabled={disabled || marketCents == null}
                onChange={onTogglePrice}
              />
              <span>Use the market price</span>
              <span className="text-xs text-faint">
                {marketCents != null
                  ? `${money(selected.marketValueCents)} → ${money(marketCents)}`
                  : "No market price for this scan."}
              </span>
            </label>
          </div>
          <p className="text-xs text-faint">
            Confirming keeps this item, its stock and its breaks. Nothing else about it changes unless you tick a box
            above or edit the price.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            type="search"
            className="field"
            placeholder="Barcode, name or set"
            aria-label="Search your sealed items"
            value={search}
            disabled={disabled}
            onChange={(e) => onSearch(e.target.value)}
          />
          {status.kind === "error" && <p className="field-error text-xs">{status.message}</p>}
          {candidates.length === 0 ? (
            <p className="text-xs text-faint">
              {status.kind === "loading" ? "Searching…" : "You have no hand-made sealed items."}
            </p>
          ) : (
            <ul className="space-y-2">
              {!matched && (
                <li className="text-xs text-faint">{`Nothing matches “${search.trim()}” — all hand-made items:`}</li>
              )}
              {candidates.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onPick(item.id)}
                    disabled={disabled}
                    aria-label={`Link to ${item.name}`}
                    className="w-full rounded-row border border-line bg-surface p-3 text-left transition-colors hover:border-muted"
                  >
                    <ItemLine item={item} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
